import type { AgentCard as AgentCardDto } from '@ralph/contracts'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { accentVars } from '@/components/marketplace/accents.ts'
import { Badge } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'
import { AgentAvatar } from './AgentAvatar.tsx'
import { AgentBadges, OfficialMark } from './AgentBadges.tsx'
import { ownerName } from './agent-identity.ts'

/**
 * One gallery card.
 *
 * Holds exactly what the PRD allows a card to hold — icon, name, tagline, owner, badges and
 * one trust mark. The owner is a person or a group (PRD-26 D-19); a group wears its avatar, the
 * shape that tells a group from a person before anybody reads the name. Freshness, version and
 * description live on the listing page; a card that grows a fourth line of metadata stops being
 * scannable, which is the only job it has.
 *
 * `state` and `health` arrive only for the viewer's own listings, so the draft chip and the
 * needs-attention line are gated on their presence rather than on a role check here — the
 * server already decided who is allowed to know an agent is in trouble.
 *
 * **The whole card is one link, and "Official" is inside it.** Both halves are load-bearing:
 * the browser suite finds a card by the accessible name of a single `<a>` and then asserts
 * the mark is part of that link's text, so a corner ribbon on the wrapping `<li>` — or a
 * second link for the owner — would pass review and fail the suite.
 *
 * The link paints itself with the agent's own colour twice: `accentVars` feeds the accent to
 * `.accent-wash`, which mixes it into whichever theme is active instead of over it, and the
 * same value fills the identity tile. Nothing here writes a colour of its own.
 */
export const AgentCard = ({ card, to }: { card: AgentCardDto; to: string }) => {
  // The lifecycle map is built inside the body rather than at module scope. Hoisted, it would
  // capture whichever language imported this file first and keep rendering it after a switch,
  // with no type error and no failing test to say so (PRD-27 F-3, LOC-012).
  const t = useT().marketplace.gallery.card
  const stateLabels = {
    draft: t.stateDraft,
    archived: t.stateArchived,
    suspended: t.stateSuspended,
    published: null
  }
  const stateLabel = card.state ? stateLabels[card.state] : null

  return (
    <Link
      to={to}
      style={accentVars(card.accent)}
      /*
       * Two of these classes look decorative and are not.
       *
       * `text-fg` — the base layer paints every `<a>` in the primary colour and recolours it
       * on hover, which would leave the whole card violet and then flip it on approach.
       *
       * The focus shadow — `.card` sets `box-shadow` from the components layer, which sits
       * above the base layer's `:focus-visible` ring, so the ring never paints on a card
       * unless a utility restates it. Without this the card has no keyboard focus indicator
       * at all, which is the kind of regression that only shows up in an audit.
       */
      className="card card-hover accent-wash flex h-full flex-col rounded-card p-[15px] text-fg focus-visible:[box-shadow:var(--ring)]"
    >
      <span className="flex items-start gap-[11px]">
        <AgentAvatar displayName={card.displayName} icon={card.icon} accent={card.accent} />

        {/*
          The right inset reserves the corner the gallery's detail-sheet control sits in
          (PRD-22 DTL-002). That control is absolutely positioned by the list item, outside
          this link — it has to be, because a card is one link — so nothing in the flow knows
          it is there, and a long name truncated to the card's full width ends up underneath
          it. Twenty-two pixels is its 26px footprint minus the card padding it overhangs, and
          it costs the name two characters on the one card in twenty that is long enough to
          notice.
        */}
        <span className="min-w-0 flex-1 pt-px pr-[22px]">
          <span className="block truncate text-[14.5px] font-semibold tracking-[-0.022em]">{card.displayName}</span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="readout inline-flex min-w-0 items-center gap-1 text-fg-subtle">
              {card.owner.kind === 'group' && <GroupAvatar group={card.owner.group} size={16} />}
              <span className="min-w-0 truncate">{t.ownerLine(ownerName(card.owner))}</span>
            </span>
            {card.official && <OfficialMark />}
            {stateLabel && <Badge>{stateLabel}</Badge>}
          </span>
        </span>
      </span>

      {/*
        Two lines of tagline, and the space for two whether or not there are two. A listing
        with a one-line summary next to one with two would otherwise shift the hairline and
        the arrow below it, and a grid of cards whose furniture sits at six different heights
        reads as six different components.
      */}
      <span className="mt-3 line-clamp-2 min-h-[39px] text-pretty text-[12.5px] leading-[1.55] text-fg-muted">
        {card.tagline}
      </span>

      {/* The closing line: what it brings, and the arrow that says this whole card goes
          somewhere. `mt-auto` pins it to the bottom so it lines up across a row. */}
      <span className="mt-auto flex items-center gap-2 border-t border-rule pt-[11px]">
        <AgentBadges badges={card.badges} className="min-w-0" />
        <ArrowRight size={12} strokeWidth={2.1} aria-hidden className="ml-auto shrink-0 text-fg-subtle" />
      </span>

      {/* Icon AND text, never colour alone (AGM-124). */}
      {card.health === 'attention' && (
        <span className="mt-2.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-alert">
          <AlertTriangle size={12} strokeWidth={2.2} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {t.needsAttention} <span className="text-fg-muted">{t.needsAttentionHint}</span>
          </span>
        </span>
      )}
    </Link>
  )
}
